import Decimal from 'break_infinity.js'
import { GameState, UpgradeId } from '../types'
import { upgrades } from '../data/upgrades'
import { calculateTPS, getBuildingCount, getTotalBuildingCount } from './buildingSystem'

const BASE_CLICK_VALUE = new Decimal(1)

export const calculateClickMultiplier = (state: GameState): Decimal => {
  let multiplier = new Decimal(1)

  for (const upgradeId of state.upgrades) {
    const upgrade = upgrades.get(upgradeId as UpgradeId)
    if (upgrade === undefined) continue

    const effect = upgrade.effect

    // Click doublers
    if (effect.type === 'doubler' && effect.targetBuilding === 'click') {
      multiplier = multiplier.mul(2)
    }
    // Click synergy (e.g., +1% per Coding Assistant)
    else if (effect.type === 'synergy' && effect.targetBuilding === 'click') {
      let sourceCount: Decimal
      if (effect.sourceBuilding === 'all') {
        sourceCount = getTotalBuildingCount(state)
      } else {
        sourceCount = getBuildingCount(state, effect.sourceBuilding)
      }
      const synergyBonus = sourceCount.mul(effect.bonusPercentage).div(100)
      multiplier = multiplier.mul(new Decimal(1).add(synergyBonus))
    }
  }

  return multiplier
}

export const calculateClickTpsScaling = (state: GameState): Decimal => {
  let totalPercentage = new Decimal(0)

  for (const upgradeId of state.upgrades) {
    const upgrade = upgrades.get(upgradeId as UpgradeId)
    if (upgrade === undefined) continue

    const effect = upgrade.effect
    if (effect.type === 'clickScaling') {
      totalPercentage = totalPercentage.add(effect.tpsPercentage)
    }
  }

  // Returns the percentage (e.g., 41 for 41%)
  return totalPercentage
}

export const calculateClickValue = (state: GameState): Decimal => {
  // Base click value × click doublers
  const baseWithMultipliers = BASE_CLICK_VALUE.mul(calculateClickMultiplier(state))

  // Plus (TPS × sum of click scaling percentages / 100)
  const tps = calculateTPS(state)
  const scalingPercentage = calculateClickTpsScaling(state)
  const tpsBonus = tps.mul(scalingPercentage).div(100)

  return baseWithMultipliers.add(tpsBonus)
}

export const applyClick = (state: GameState): GameState => {
  const clickValue = calculateClickValue(state)
  return {
    ...state,
    tokens: state.tokens.add(clickValue),
    totalClicks: state.totalClicks.add(1),
  }
}
